/**
 * TanStack Query hooks and key factory for the principal directory and vendor company membership.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { grantsApi } from "@/services/grantsApi"
import { type PrincipalGrant, principalsApi } from "@/services/principalsApi"

import { useAllPages } from "./useAllPages"
import { grantKeys } from "./useGrants"
import { vendorKeys } from "./useVendors"

const principalKeys = {
  all: ["principals"] as const,
  lists: () => [...principalKeys.all, "list"] as const,
  list: () => [...principalKeys.lists()] as const,
  details: () => [...principalKeys.all, "detail"] as const,
  detail: (principalId: string) => [...principalKeys.details(), principalId] as const,
}

/**
 * Fetch every principal in the admin directory, newest first.
 */
export function usePrincipalsList(pageSize = 50) {
  const { items, isPending, error } = useAllPages(principalKeys.list(), async (nextToken) => {
    const page = await principalsApi.listPrincipals({ limit: pageSize, nextToken })
    return { items: page.principals, nextToken: page.nextToken }
  })
  return { principals: items, isPending, error }
}

/**
 * Fetch one principal's identity, vendor company, and grants.
 */
export function usePrincipalDetail(principalId: string) {
  return useQuery({
    queryKey: principalKeys.detail(principalId),
    queryFn: () => principalsApi.getPrincipal(principalId),
  })
}

/**
 * Set a principal's vendor company and refetch the vendor pages and DTA vendor picker, which list
 * members.
 */
export function useAssignPrincipalCompany() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ principalId, companyId }: { principalId: string; companyId: string }) =>
      principalsApi.assignCompany(principalId, companyId),
    onSuccess: (principal) => {
      queryClient.setQueryData(principalKeys.detail(principal.principal_id), principal)
      invalidateMembership(queryClient)
    },
  })
}

/**
 * Revoke a principal's grant and refresh the principal and the resource's grants.
 */
export function useRevokePrincipalGrant(principalId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (grant: PrincipalGrant) =>
      grantsApi.revokeGrant(
        { type: grant.resource_type === "DTA" ? "dta" : "study", id: grant.resource_id },
        principalId,
        grant.capability_set,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: principalKeys.detail(principalId) })
      void queryClient.invalidateQueries({ queryKey: principalKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: grantKeys.lists() })
    },
  })
}

/**
 * Invalidate every cached list of vendor company members.
 */
export function invalidateMembership(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: principalKeys.all })
  void queryClient.invalidateQueries({ queryKey: vendorKeys.details() })
  void queryClient.invalidateQueries({ queryKey: grantKeys.grantCandidateLists() })
}
